'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { formatarPrecoDePlano } from '@/shared/formatters/currency';
import { applyCnpjMask, formatTelefone, isValidEmail, isValidTelefone } from '@/shared/utils';
import { normalizeCpfCnpj } from '@/shared/utils/documentValidation';
import { urlPagamentoSegura } from '@/shared/utils/pagamentoUrl';
import { reportError } from '@/shared/utils/reportError';
import { PLANO_APRESENTACAO, STEPPER_LABELS, STEPPER_STEP_ORDER } from '../constants';
import type { CadastroForm, PlanoApi, PlanoTier, PlanoView, SelfStep, WizardStepView } from '../types';
import { carregarPlanosSelf, consultarCnpjSelf, consultarPagamentoSelf, criarSessaoSelf, enviarCadastroSelf, iniciarPagamentoSelf, limparAvisoSessao, resetCnpjStatus, selectSelfCadastroErro, selectSelfCarregandoPlanos, selectSelfCnpjErro, selectSelfCnpjStatus, selectSelfEnviandoCadastro, selectSelfForm, selectSelfMedicaoAutorizada, selectSelfIniciandoPagamento, selectSelfInvoiceUrl, selectSelfPagamentoErro, selectSelfPagamentoStatus, selectSelfPlanos, selectSelfPlanosErro, selectSelfPlanoTier, selectSelfPollingPagamentoExpirado, selectSelfSessaoExpirada, selectSelfStep, setCampo, setPlanoTier, setStep, setTermosAceitos, setMedicaoAutorizada } from '../store';
type CampoTexto = keyof Omit<CadastroForm, 'termosAceitos'>;
const toPlanoView = (plano: PlanoApi): PlanoView => {
  const apresentacao = PLANO_APRESENTACAO[plano.tier];
  return {
    variationId: plano.variationId,
    tier: plano.tier,
    nome: plano.nome,
    preco: formatarPrecoDePlano(plano.precoMensal),
    precoSufixo: '/mês',
    franquia: `${plano.franquiaEnvios.toLocaleString('pt-BR')} envios/mês`,
    porEnvio: plano.porEnvio,
    destaque: plano.destaque || undefined,
    badge: plano.badge ?? undefined,
    canais: apresentacao?.canais ?? [],
    plataforma: apresentacao?.plataforma ?? [],
    ctaLabel: apresentacao?.ctaLabel ?? 'Começar agora'
  };
};
const abrirAbaCheckout = (): Window | null => {
  const aba = window.open('', '_blank');
  if (aba) aba.opener = null;
  return aba;
};
const apontarAbaCheckout = (aba: Window | null, url: string | null) => {
  if (!urlPagamentoSegura(url)) {
    aba?.close();
    if (url) {
      reportError(new Error('Link de pagamento recusado pelo guard de URL'), {
        source: 'notificacaoSelf.abrirCheckout'
      });
    }
    return;
  }
  if (aba) {
    aba.location.href = url;
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};
export const useNotificacaoSelf = () => {
  const $ = _c(100);
  const dispatch = useAppDispatch();
  const step = useAppSelector(selectSelfStep);
  const form = useAppSelector(selectSelfForm);
  const medicaoAutorizada = useAppSelector(selectSelfMedicaoAutorizada);
  const cnpjStatus = useAppSelector(selectSelfCnpjStatus);
  const cnpjErro = useAppSelector(selectSelfCnpjErro);
  const planosApi = useAppSelector(selectSelfPlanos);
  const carregandoPlanos = useAppSelector(selectSelfCarregandoPlanos);
  const planosErro = useAppSelector(selectSelfPlanosErro);
  const enviandoCadastro = useAppSelector(selectSelfEnviandoCadastro);
  const cadastroErro = useAppSelector(selectSelfCadastroErro);
  const confirmandoRevisao = useAppSelector(selectSelfIniciandoPagamento);
  const planoTier = useAppSelector(selectSelfPlanoTier);
  const pagamentoStatus = useAppSelector(selectSelfPagamentoStatus);
  const pagamentoErro = useAppSelector(selectSelfPagamentoErro);
  const invoiceUrl = useAppSelector(selectSelfInvoiceUrl);
  const pollingExpirado = useAppSelector(selectSelfPollingPagamentoExpirado);
  const sessaoExpirada = useAppSelector(selectSelfSessaoExpirada);
  let t0;
  let t1;
  if ($[0] !== carregandoPlanos || $[1] !== dispatch || $[2] !== planosApi.length || $[3] !== planosErro) {
    t0 = () => {
      if (planosApi.length > 0 || carregandoPlanos || planosErro) {
        return;
      }
      dispatch(carregarPlanosSelf());
    };
    t1 = [planosApi.length, carregandoPlanos, planosErro, dispatch];
    $[0] = carregandoPlanos;
    $[1] = dispatch;
    $[2] = planosApi.length;
    $[3] = planosErro;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  let t2;
  if ($[6] !== planosApi) {
    t2 = planosApi.map(toPlanoView);
    $[6] = planosApi;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const planos = t2;
  const currentIndex = STEPPER_STEP_ORDER[step] ?? 0;
  let t3;
  if ($[8] !== currentIndex) {
    t3 = STEPPER_LABELS.map((label, index) => ({
      label,
      filled: index <= currentIndex,
      active: index === currentIndex
    }));
    $[8] = currentIndex;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const stepperSteps = t3;
  let t4;
  if ($[10] !== planoTier || $[11] !== planos) {
    let t5;
    if ($[13] !== planoTier) {
      t5 = plano => plano.tier === planoTier;
      $[13] = planoTier;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t4 = planos.find(t5);
    $[10] = planoTier;
    $[11] = planos;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const planoSelecionado = t4;
  let t5;
  if ($[15] !== form.responsavelEmail) {
    t5 = isValidEmail(form.responsavelEmail);
    $[15] = form.responsavelEmail;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const emailResponsavelValido = t5;
  let t6;
  if ($[17] !== form.telefone) {
    t6 = isValidTelefone(form.telefone);
    $[17] = form.telefone;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const telefoneValido = t6;
  let t7;
  if ($[19] !== cnpjStatus || $[20] !== emailResponsavelValido || $[21] !== form.nomeFantasia || $[22] !== form.razaoSocial || $[23] !== form.responsavelNome || $[24] !== form.termosAceitos || $[25] !== planos.length || $[26] !== telefoneValido) {
    t7 = (cnpjStatus === "ok" || cnpjStatus === "naoEncontrado") && form.razaoSocial.trim().length > 0 && form.nomeFantasia.trim().length > 0 && telefoneValido && form.responsavelNome.trim().length > 0 && emailResponsavelValido && form.termosAceitos && planos.length > 0;
    $[19] = cnpjStatus;
    $[20] = emailResponsavelValido;
    $[21] = form.nomeFantasia;
    $[22] = form.razaoSocial;
    $[23] = form.responsavelNome;
    $[24] = form.termosAceitos;
    $[25] = planos.length;
    $[26] = telefoneValido;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const podeAvancarCadastro = t7;
  let t8;
  if ($[28] !== dispatch) {
    t8 = destino => dispatch(setStep(destino));
    $[28] = dispatch;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const goTo = t8;
  let t9;
  if ($[30] !== cnpjStatus || $[31] !== dispatch) {
    t9 = (campo, valor) => {
      if (campo === "cnpj") {
        const cnpjMascarado = applyCnpjMask(valor);
        dispatch(setCampo({
          campo,
          valor: cnpjMascarado
        }));
        if (normalizeCpfCnpj(cnpjMascarado).length === 14) {
          dispatch(consultarCnpjSelf());
        } else {
          if (cnpjStatus !== "idle") {
            dispatch(resetCnpjStatus());
          }
        }
        return;
      }
      if (campo === "telefone") {
        dispatch(setCampo({
          campo,
          valor: formatTelefone(valor)
        }));
        return;
      }
      dispatch(setCampo({
        campo,
        valor
      }));
    };
    $[30] = cnpjStatus;
    $[31] = dispatch;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const atualizarCampo = t9;
  let t10;
  if ($[33] !== dispatch || $[34] !== form.termosAceitos) {
    t10 = () => dispatch(setTermosAceitos(!form.termosAceitos));
    $[33] = dispatch;
    $[34] = form.termosAceitos;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  const alternarTermos = t10;
  let t11;
  if ($[36] !== dispatch || $[37] !== medicaoAutorizada) {
    t11 = () => dispatch(setMedicaoAutorizada(!medicaoAutorizada));
    $[36] = dispatch;
    $[37] = medicaoAutorizada;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const alternarMedicao = t11;
  let t12;
  if ($[39] !== dispatch) {
    t12 = () => dispatch(carregarPlanosSelf());
    $[39] = dispatch;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  const recarregarPlanos = t12;
  let t13;
  if ($[41] !== dispatch || $[42] !== enviandoCadastro || $[43] !== podeAvancarCadastro) {
    t13 = () => {
      if (!podeAvancarCadastro || enviandoCadastro) {
        return;
      }
      dispatch(limparAvisoSessao());
      dispatch(enviarCadastroSelf({
        irParaRevisao: true
      }));
    };
    $[41] = dispatch;
    $[42] = enviandoCadastro;
    $[43] = podeAvancarCadastro;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  const enviarCadastro = t13;
  let t14;
  if ($[45] !== confirmandoRevisao || $[46] !== dispatch || $[47] !== enviandoCadastro || $[48] !== form.termosAceitos || $[49] !== planoTier) {
    t14 = async () => {
      if (confirmandoRevisao || enviandoCadastro) {
        return;
      }
      if (!form.termosAceitos) {
        dispatch(setStep("cadastro"));
        return;
      }
      const aba = planoTier === "free" ? null : abrirAbaCheckout();
      const cadastro = await dispatch(enviarCadastroSelf({
        irParaRevisao: false
      }));
      if (!enviarCadastroSelf.fulfilled.match(cadastro)) {
        aba?.close();
        return;
      }
      const resultado = await dispatch(iniciarPagamentoSelf());
      if (!iniciarPagamentoSelf.fulfilled.match(resultado) || resultado.payload.convertido) {
        aba?.close();
        return;
      }
      apontarAbaCheckout(aba, resultado.payload.invoiceUrl);
    };
    $[45] = confirmandoRevisao;
    $[46] = dispatch;
    $[47] = enviandoCadastro;
    $[48] = form.termosAceitos;
    $[49] = planoTier;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  const confirmarRevisao = t14;
  let t15;
  if ($[51] !== invoiceUrl) {
    t15 = () => apontarAbaCheckout(null, invoiceUrl);
    $[51] = invoiceUrl;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  const reabrirCheckout = t15;
  let t16;
  if ($[53] !== confirmandoRevisao || $[54] !== dispatch) {
    t16 = async () => {
      if (confirmandoRevisao) {
        return;
      }
      const aba_0 = abrirAbaCheckout();
      const resultado_0 = await dispatch(iniciarPagamentoSelf());
      if (!iniciarPagamentoSelf.fulfilled.match(resultado_0) || resultado_0.payload.convertido) {
        aba_0?.close();
        return;
      }
      apontarAbaCheckout(aba_0, resultado_0.payload.invoiceUrl);
    };
    $[53] = confirmandoRevisao;
    $[54] = dispatch;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  const tentarPagamentoNovamente = t16;
  let t17;
  if ($[56] !== dispatch) {
    t17 = () => dispatch(consultarPagamentoSelf());
    $[56] = dispatch;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  const verificarPagamentoAgora = t17;
  let t18;
  if ($[58] !== dispatch) {
    t18 = tier => {
      dispatch(setPlanoTier(tier));
      dispatch(setStep("cadastro"));
    };
    $[58] = dispatch;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  const escolherPlano = t18;
  let t19;
  if ($[60] !== dispatch) {
    t19 = tier_0 => {
      dispatch(setPlanoTier(tier_0));
      dispatch(setStep("revisao"));
    };
    $[60] = dispatch;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  const trocarPlano = t19;
  let t20;
  if ($[62] !== dispatch) {
    t20 = async rota => {
      await dispatch(criarSessaoSelf());
      window.location.assign(rota);
    };
    $[62] = dispatch;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  const sairPara = t20;
  let t21;
  if ($[64] !== alternarMedicao || $[65] !== alternarTermos || $[66] !== atualizarCampo || $[67] !== cadastroErro || $[68] !== carregandoPlanos || $[69] !== cnpjErro || $[70] !== cnpjStatus || $[71] !== confirmandoRevisao || $[72] !== confirmarRevisao || $[73] !== emailResponsavelValido || $[74] !== enviandoCadastro || $[75] !== enviarCadastro || $[76] !== escolherPlano || $[77] !== form || $[78] !== goTo || $[79] !== invoiceUrl || $[80] !== medicaoAutorizada || $[81] !== pagamentoErro || $[82] !== pagamentoStatus || $[83] !== planoSelecionado || $[84] !== planoTier || $[85] !== planos || $[86] !== planosErro || $[87] !== podeAvancarCadastro || $[88] !== pollingExpirado || $[89] !== reabrirCheckout || $[90] !== recarregarPlanos || $[91] !== sairPara || $[92] !== sessaoExpirada || $[93] !== step || $[94] !== stepperSteps || $[95] !== telefoneValido || $[96] !== tentarPagamentoNovamente || $[97] !== trocarPlano || $[98] !== verificarPagamentoAgora) {
    t21 = {
      step,
      form,
      medicaoAutorizada,
      cnpjStatus,
      cnpjErro,
      planos,
      carregandoPlanos,
      planosErro,
      enviandoCadastro,
      cadastroErro,
      confirmandoRevisao,
      planoTier,
      planoSelecionado,
      pagamentoStatus,
      pagamentoErro,
      invoiceUrl,
      pollingExpirado,
      sessaoExpirada,
      stepperSteps,
      emailResponsavelValido,
      telefoneValido,
      podeAvancarCadastro,
      goTo,
      atualizarCampo,
      alternarTermos,
      alternarMedicao,
      recarregarPlanos,
      enviarCadastro,
      confirmarRevisao,
      reabrirCheckout,
      tentarPagamentoNovamente,
      verificarPagamentoAgora,
      escolherPlano,
      trocarPlano,
      sairPara
    };
    $[64] = alternarMedicao;
    $[65] = alternarTermos;
    $[66] = atualizarCampo;
    $[67] = cadastroErro;
    $[68] = carregandoPlanos;
    $[69] = cnpjErro;
    $[70] = cnpjStatus;
    $[71] = confirmandoRevisao;
    $[72] = confirmarRevisao;
    $[73] = emailResponsavelValido;
    $[74] = enviandoCadastro;
    $[75] = enviarCadastro;
    $[76] = escolherPlano;
    $[77] = form;
    $[78] = goTo;
    $[79] = invoiceUrl;
    $[80] = medicaoAutorizada;
    $[81] = pagamentoErro;
    $[82] = pagamentoStatus;
    $[83] = planoSelecionado;
    $[84] = planoTier;
    $[85] = planos;
    $[86] = planosErro;
    $[87] = podeAvancarCadastro;
    $[88] = pollingExpirado;
    $[89] = reabrirCheckout;
    $[90] = recarregarPlanos;
    $[91] = sairPara;
    $[92] = sessaoExpirada;
    $[93] = step;
    $[94] = stepperSteps;
    $[95] = telefoneValido;
    $[96] = tentarPagamentoNovamente;
    $[97] = trocarPlano;
    $[98] = verificarPagamentoAgora;
    $[99] = t21;
  } else {
    t21 = $[99];
  }
  return t21;
};
export type UseNotificacaoSelfReturn = ReturnType<typeof useNotificacaoSelf>;